/**
 * Account management: declaring a master account, leaving one and deleting the account, each
 * shown only when it applies (`accountActions`).
 */
import { useQuery } from '@apollo/client/react';
import { Permission } from '@insio/shared-types';
import { usePermissions } from '@/features/auth';
import { GetProfileDocument } from '@/graphql';
import { accountActions, accountActionsSubjectOf } from './account/account-actions';
import { DeleteAccountSection } from './organization/DeleteAccountSection';
import { LeaveMasterAccountSection } from './organization/LeaveMasterAccountSection';
import { MasterAccountToggleSection } from './organization/MasterAccountToggleSection';

export function AccountManagementSettings() {
  const { hasPermission } = usePermissions();
  // Shown from the menu's cache at once, then re-read: an office may have connected meanwhile.
  const { data } = useQuery(GetProfileDocument, { fetchPolicy: 'cache-and-network' });
  const account = data?.profile?.account;
  const subject = accountActionsSubjectOf(account);
  if (!account || !subject) return null;

  const managingMasterAccount = account.managingMasterAccount ?? null;
  const actions = accountActions(subject, hasPermission(Permission.ACCOUNT_SETTINGS));

  return (
    <div className='space-y-lg' data-testid='account-management'>
      {actions.declareMasterAccount && <MasterAccountToggleSection />}
      {actions.leaveMasterAccount && managingMasterAccount && (
        <LeaveMasterAccountSection masterAccountName={managingMasterAccount.name} />
      )}
      {actions.deleteAccount && <DeleteAccountSection />}
    </div>
  );
}
